import { useDispatch, useSelector } from 'react-redux';

import { getTranslation } from 'common/react/utils/translations';
import { setPlayerModal } from 'common/store/modules/player-awards/player-modal';
import { leaderboardFilterSelector } from 'common/store/modules/player-awards/leaderboard/selectors';
import { getPredictorTrackingLabel } from '../components/table/utils';

/**
 * Shared player-modal-opening handler for the Brownlow/B&F leaderboard, used
 * by both the desktop (PlayerCell) and mobile (MobilePlayerCell) row
 * components so the dispatch + view_modal tracking + search-selection
 * tracking logic isn't duplicated between them
 *
 * @param {object} params
 * @param {object} params.player
 * @param {string} params.seasonPid
 * @param {object} params.nav - navSelector state
 * @param {boolean} params.shouldHidePredicted
 * @param {boolean} params.isBrownlowV2
 * @param {boolean} params.isAFLW
 * @param {number} params.count
 * @param {Function} params.getFiltersString - lazily invoked to get the composeFiltersString result,
 * only called when there's an active search query - must not be called/evaluated eagerly on every render
 * @param {string} params.modifier
 * @returns {Function} activatePlayerModal(event) click/keydown handler
 */
export const useActivatePlayerModal = ({
    player,
    seasonPid,
    nav,
    shouldHidePredicted,
    isBrownlowV2,
    isAFLW,
    count,
    getFiltersString,
    modifier
}) => {
    const dispatch = useDispatch();
    const query = useSelector(leaderboardFilterSelector);

    return (event) => {
        if (
            event.keyCode &&
            event.keyCode !== PULSE.app.common.CONSTANTS.KEY_CODES.ENTER &&
            event.keyCode !== PULSE.app.common.CONSTANTS.KEY_CODES.SPACE
        ) {
            return;
        }

        dispatch(
            setPlayerModal({
                visible: true,
                playerId: player.playerId,
                team: player?.team,
                seasonPid: seasonPid,
                seasonId: nav.season.id,
                player,
                shouldHidePredicted: shouldHidePredicted,
                isPredictor: isBrownlowV2,
                modifier
            })
        );

        /* eslint-disable camelcase */
        PULSE.app.tracking.recordEvent(
            PULSE.app.common.CONSTANTS.TRACKING.EVENTS.MODAL.VIEW,
            {
                eventData: {
                    modal_name: isBrownlowV2
                        ? getPredictorTrackingLabel(isAFLW, 'player profile')
                        : getTranslation(
                              'label.brownlow.tracker.leaderboard.full'
                          )
                }
            }
        );
        /* eslint-enable camelcase */

        if (query.query.length) {
            PULSE.app.analytics.playerSelectionTracking(
                query.query,
                '',
                'Brownlow tracker',
                `${player.givenName} ${player.surname}`,
                count,
                getFiltersString()
            );
        }
    };
};
